import React, { useState, useEffect } from 'react';
import {
    Typography, Divider, Select, Button,
    Progress, List, Space, message, Input, Modal
} from 'antd';
import {
    CheckCircleFilled, FileOutlined,
    CheckCircleOutlined, PlusOutlined, WarningOutlined, CloseOutlined
} from '@ant-design/icons';
import { BlockBlobClient } from '@azure/storage-blob';
import { v4 as uuidv4 } from 'uuid';
import ApiClient from "../api";
import { useWebSocket } from '../WebSocketContext';
import AdjudicationNotifier from '../components/AdjudicationNotifier';

const api_client = new ApiClient();
const { Title, Text } = Typography;

const MAX_FILES = 3;

const UploadPage = () => {
    const { messages: wsMessages } = useWebSocket();

    // Data States
    const [insurersData, setInsurersData]           = useState([]);
    const [availablePolicies, setAvailablePolicies] = useState([]);
    const [insurerPolicies, setInsurerPolicies]     = useState([]);
    const [rulesModalVisible, setRulesModalVisible] = useState(false);
    const [targetPolicy, setTargetPolicy]           = useState('');
    const [roleType, setRoleType]                   = useState(null);
    const [license, setLicense] = useState(null);

    // POC trial counter (top-right badge)
    const [pocCounter, setPocCounter] = useState(null); // { usage_count, max_count, poc_active }

    // Form States
    const [selectedInsurer, setSelectedInsurer]     = useState(null);
    const [selectedPolicy, setSelectedPolicy]       = useState(null);
    const [fileList, setFileList]                   = useState([]);
    const [isUploading, setIsUploading]             = useState(false);
    const [ipNo, setIpNo]                           = useState('');
    const [ipNoError, setIpNoError]                 = useState(false);
    const [successModalOpen, setSuccessModalOpen]   = useState(false);
    const [submittedIpNo, setSubmittedIpNo]         = useState('');
    const [corporate, setCorporate]                 = useState('');
    const [memberLevelTerm, setMemberLevelTerm]     = useState('');

    // ── Helper: fetch/refresh the POC counter badge ──────────
    const refreshPocCounter = async (lic) => {
        if (!lic) return;
        try {
            const counter = await api_client.get_counter(lic);
            setPocCounter({
                usage_count: counter?.usage_count ?? 0,
                max_count: counter?.max_count ?? 100,
                poc_active: counter?.poc_active ?? true
            });
        } catch (err) {
            console.error('[AudiDox POC] Failed to fetch counter:', err);
        }
    };

    // ── 1. Fetch Dynamic Data on Mount ───────────────────────
    useEffect(() => {
        const loadInsurers = async () => {
            try {
                const user_access = JSON.parse(localStorage.getItem('user_access'));
                const role = user_access?.roletype;
                setRoleType(role);
                setLicense(user_access?.license);
                const data = await api_client.get_insurers();
                if (data) setInsurersData(data);
                if (role === 'INSURER') {
                    const policies = await api_client.getpolicynumberbylicense(user_access.license);
                    setInsurerPolicies(policies || []);
                }
                // Load the POC trial counter on page load/refresh
                await refreshPocCounter(user_access?.license);
            } catch (err) {
                message.error("Failed to load insurer configurations.");
            }
        };
        loadInsurers();
    }, []);

    // ── 2. Linked Dropdown Logic (CLINIC role) ───────────────
    const handleInsurerChange = (license) => {
        const insurer = insurersData.find(i => i.license === license);
        setSelectedInsurer(license);
        setCorporate(insurer?.company || '');
        setAvailablePolicies(insurer ? insurer.policies : []);
        setSelectedPolicy(null);
    };

    const resetForm = () => {
        setSelectedInsurer(null);
        setSelectedPolicy(null);
        setAvailablePolicies([]);
        setFileList([]);
        setIpNo('');
        setIpNoError(false);
        setSuccessModalOpen(false);
        setSubmittedIpNo('');
        setCorporate('');
        setMemberLevelTerm('');
    };

    const performRealUpload = async (file) => {
        try {
            const response = await api_client.get_blob_post_url(file.name);
            const { uploadUrl } = response;
            const blobClient = new BlockBlobClient(uploadUrl);

            await blobClient.uploadData(file, {
                onProgress: (progress) => {
                    const percent = Math.round((progress.loadedBytes / file.size) * 100);
                    updateFileStatus(file.uid, 'uploading', percent);
                }
            });

            const finalUrl = uploadUrl.split('?')[0];
            updateFileStatus(file.uid, 'done', 100, finalUrl);
            return finalUrl;
        } catch (error) {
            updateFileStatus(file.uid, 'error', 0);
            message.error(`${file.name} failed to upload.`);
        }
    };

    const updateFileStatus = (uid, status, percent, finalUrl = null) => {
        setFileList(prev => prev.map(f =>
            f.uid === uid ? { ...f, status, percent, finalUrl } : f
        ));
    };

    const removeFile = (uid) => {
        setFileList(prev => prev.filter(f => f.uid !== uid));
    };

    function isPrimaryBillFilename(filename) {
        const d = filename.toUpperCase();
        if (!d.includes('BILL')) return false;
        if (d.includes('SUMMARY')) return false; // distinct doc, keep
        if (d.includes('RECEIPT')) return false; // distinct doc, keep
        return true; // catches "BILL", "FINAL BILL", "AMENDED BILL", "AMENDED FINAL BILL", etc.
    }

    function isAmended(filename) {
        return /AMENDED/i.test(filename);
    }

    /**
     * Silently drops non-amended bill files from this claim's fileList when
     * an amended bill is also present, so Gemini never sees both.
     */
    function filterSupersededBills(fileList) {
        const billFiles = fileList.filter(f => {
            const name = f.name || f.originFileObj?.name || '';
            return isPrimaryBillFilename(name);
        });

        const hasAmended = billFiles.some(f => isAmended(f.name || f.originFileObj?.name || ''));
        if (!hasAmended) return fileList; // nothing superseded, leave everything alone

        return fileList.filter(f => {
            const name = f.name || f.originFileObj?.name || '';
            if (!isPrimaryBillFilename(name)) return true; // not a bill doc, keep
            if (isAmended(name)) return true; // amended bill, keep
            console.log(`[AudiDox] Superseded bill excluded pre-upload: "${name}"`);
            return false; // non-amended bill, drop silently
        });
    }

    const handleConfirm = async () => {

        setIpNoError(false);

        // 1. Run POC Guard Check BEFORE toggling upload states
        try {
            const counter = await api_client.get_counter(license);

            const usage_count = counter?.usage_count ?? 0;
            const poc_active = counter?.poc_active ?? true;
            const max_count = counter?.max_count ?? 100;

            // Keep the top-right badge in sync with the latest fetch
            setPocCounter({ usage_count, max_count, poc_active });

            if (!poc_active) {
                alert("Your Proof of Concept access is currently inactive. Please contact AudiDox support.");
                return; // STOP EXECUTION
            }

            if ((usage_count + 1) > max_count) {
                alert(`The Proof Of Concept free trial was limited to ${max_count} claims. Thank you for testing AudiDox!`);
                return; // STOP EXECUTION
            }

            console.log(`[AudiDox POC] Check passed. Usage: ${usage_count + 1}/${max_count}`);
        } catch (pocErr) {
            console.error("[AudiDox POC] Counter validation failed:", pocErr);
            message.error("Unable to verify POC status. Please try again.");
            return; // STOP EXECUTION
        }

        // 2. State checks passed -> Start upload UI state
        const stack_id = uuidv4();
        setIsUploading(true);
        // 3. Silently drop non-amended bills when an amended version exists
        const dedupedFileList = filterSupersededBills(fileList);
        try {
            const uploadPromises = dedupedFileList
                .filter(f => f.status !== 'done')
                .map(f => performRealUpload(f.originFileObj));

            const results = await Promise.all(uploadPromises);
            const successfulUrls = results.filter(url => url !== undefined);

            const user_access = JSON.parse(localStorage.getItem('user_access'));

            const _license = selectedInsurer ?? user_access.license;
            const _corporate = corporate || user_access.corporate;
            const getUTCOffset = () => -new Date().getTimezoneOffset() / 60;

            const payload = {
                stack_id: stack_id,
                ip_no: "NOT ASSIGNED",
                selected_policy: selectedPolicy,
                selected_insurer: _corporate,
                file_list: successfulUrls,
                email: user_access.email,
                license: _license,
                corporate: _corporate,
                name: user_access.name,
                surname: user_access.surname,
                requester_license: user_access.license,
                clock_drift: getUTCOffset(),
                member_level_term: memberLevelTerm.trim(),
                APP_SRC: 'AUDIDOX-PRO'
            };

            const dbResponse = await api_client.vertexAI(payload);
            if (dbResponse.success) {
                setSubmittedIpNo("NOT ASSIGNED");
                setSuccessModalOpen(true);
                // Refresh the badge again post-submit to reflect the incremented usage
                await refreshPocCounter(_license);
            }
        } catch (err) {
            message.error("Database registration failed.");
        } finally {
            setIsUploading(false);
        }
    };

    // Shared policy selection guard (rules_applied check)
    const handlePolicyChange = (policyNumber) => {
        const sourceList = roleType === 'INSURER' ? insurerPolicies : availablePolicies;
        const policyObj  = sourceList.find(p => p.policynumber === policyNumber);

        if (policyObj && policyObj.rules_applied === false) {
            setTargetPolicy(policyNumber);
            setRulesModalVisible(true);
            setSelectedPolicy(null);
        } else {
            setSelectedPolicy(policyNumber);
        }
    };

    const isComplete  = fileList.length > 0 && fileList.every(f => f.status === 'done');
    const isDisabled  = isUploading || successModalOpen;
    const isAtFileCap = fileList.length >= MAX_FILES;

    // ── Dropdown rendering based on role ─────────────────────
    const renderPolicySelectors = () => {
        if (roleType === 'INSURER') {
            return (
                <Select
                    placeholder="Select Policy Number"
                    style={{ width: '100%', marginBottom: 12 }}
                    value={selectedPolicy}
                    onChange={handlePolicyChange}
                    options={insurerPolicies.map(p => ({
                        label: p.policynumber,
                        value: p.policynumber
                    }))}
                    showSearch
                    optionFilterProp="label"
                />
            );
        }

        return (
            <div style={{ display: 'flex', gap: '12px', marginBottom: 12 }}>
                <Select
                    placeholder="Select Insurer"
                    style={{ flex: 1 }}
                    onChange={handleInsurerChange}
                    value={selectedInsurer}
                    options={insurersData.map(i => ({
                        label: i.company || i.license,
                        value: i.license
                    }))}
                    showSearch
                    optionFilterProp="label"
                />
                <Select
                    placeholder="Policy Number"
                    style={{ flex: 1 }}
                    disabled={!selectedInsurer}
                    value={selectedPolicy}
                    onChange={handlePolicyChange}
                    options={availablePolicies.map(p => ({
                        label: p.policynumber,
                        value: p.policynumber
                    }))}
                />
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
            {/* POC trial counter badge — top right of the screen */}
            {pocCounter && pocCounter.poc_active && (
                <div style={{
                    position: 'fixed',
                    top: 40,
                    right: 24,
                    zIndex: 1000,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    background: 'rgba(121,167,165,0.08)',
                    border: '1px solid rgba(121,167,165,0.25)',
                    borderRadius: 8,
                    padding: '6px 14px'
                }}>
                    <Text style={{ color: '#6B8585', fontSize: 11, textTransform: 'uppercase' }}>POC Trials</Text>
                    <Text style={{ color: '#79A7A5', fontSize: 13, fontFamily: 'monospace', fontWeight: 600 }}>
                        {Math.max(pocCounter.max_count - pocCounter.usage_count, 0)} of {pocCounter.max_count} left
                    </Text>
                </div>
            )}

            {/* Left pane: original upload form, width 800 (unchanged) */}
            <div style={{ width: 800, height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
                <AdjudicationNotifier wsMessages={wsMessages} />

                <div style={{
                    flexShrink: 0,
                    opacity: isDisabled ? 0.35 : 1,
                    pointerEvents: isDisabled ? 'none' : 'auto',
                    transition: 'opacity 0.4s ease'
                }}>
                    <Title level={2} style={{ color: 'var(--text-primary)', fontFamily: 'Space Grotesk' }}>Upload Files</Title>

                    {renderPolicySelectors()}

                    <input
                        type="file"
                        multiple
                        style={{ display: 'none' }}
                        id="audidox-file-input"
                        onChange={(e) => {
                            const files = Array.from(e.target.files);
                            const remainingSlots = MAX_FILES - fileList.length;

                            if (remainingSlots <= 0) {
                                message.warning(`You can only upload up to ${MAX_FILES} files per claim.`);
                                e.target.value = '';
                                return;
                            }

                            const filesToAdd = files.slice(0, remainingSlots);
                            if (files.length > remainingSlots) {
                                message.warning(`Only ${remainingSlots} more file(s) can be added (max ${MAX_FILES} per claim).`);
                            }

                            const newFiles = filesToAdd.map(file => ({
                                uid: `rc-upload-${Date.now()}-${file.name}`,
                                name: file.name,
                                status: 'ready',
                                percent: 0,
                                originFileObj: file
                            }));
                            setFileList(prev => [...prev, ...newFiles]);
                            e.target.value = '';
                        }}
                    />

                    <div style={{
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border-strong)',
                        borderRadius: '12px',
                        padding: '40px',
                        textAlign: 'center',
                        marginBottom: 20
                    }}>
                        <Title level={4} style={{ color: '#F0F8F7', marginBottom: 8 }}>
                            Upload Files
                        </Title>
                        <Text style={{ color: '#6B8585', fontSize: 12, display: 'block', marginBottom: 20 }}>
                            Please include: Discharge Summary, Invoice, and Guarantee of Payment (GOP). Max {MAX_FILES} files per claim.
                        </Text>
                        <Button
                            icon={<PlusOutlined />}
                            size="large"
                            disabled={isDisabled || isAtFileCap}
                            onClick={() => document.getElementById('audidox-file-input').click()}
                            style={{
                                background: 'rgba(121,167,165,0.08)',
                                borderColor: '#79A7A5',
                                color: '#79A7A5',
                                height: 48,
                                padding: '0 32px',
                                borderRadius: 8,
                                fontWeight: 600
                            }}
                        >
                            {isAtFileCap ? 'Max Files Reached' : 'Select Files'}
                        </Button>
                    </div>
                </div>

                <div style={{ flexGrow: 1, overflowY: 'auto', marginBottom: 16, opacity: isDisabled ? 0.35 : 1 }}>
                    <List
                        dataSource={fileList}
                        renderItem={(file) => (
                            <div style={{
                                background: '#161C1C',
                                border: '1px solid var(--border-default)',
                                borderRadius: '6px',
                                padding: '8px 12px',
                                marginBottom: 8
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: 4
                                }}>
                                    <Space size="small" style={{ maxWidth: '85%' }}>
                                        <FileOutlined style={{ color: 'var(--color-teal-mid)' }} />
                                        <Text style={{ color: '#F0F8F7', fontSize: '12px' }} ellipsis>{file.name}</Text>
                                    </Space>
                                    <Space size="small">
                                        {file.status === 'done'
                                            ? <CheckCircleFilled style={{ color: '#79A7A5' }} />
                                            : <Text style={{ fontSize: '10px', color: '#6B8585' }}>{file.percent}%</Text>
                                        }
                                        {file.status !== 'uploading' && (
                                            <CloseOutlined
                                                onClick={() => !isDisabled && removeFile(file.uid)}
                                                style={{
                                                    fontSize: 11,
                                                    color: isDisabled ? '#3A4444' : '#6B8585',
                                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                    marginLeft: 4
                                                }}
                                            />
                                        )}
                                    </Space>
                                </div>
                                <Progress percent={file.percent} strokeColor="#79A7A5" trailColor="#0D0F14" strokeWidth={3} showInfo={false} />
                            </div>
                        )}
                    />
                </div>

                <div style={{ flexShrink: 0 }}>
                    <Button
                        type="primary" block size="large"
                        loading={isUploading}
                        onClick={handleConfirm}
                        disabled={fileList.length === 0 || isComplete || isDisabled || !selectedPolicy}
                        style={{ height: 48, borderRadius: 8 }}
                    >
                        {isComplete ? 'Uploads Finished' : 'Upload to AudiDox'}
                    </Button>
                </div>

                {/* Success Modal */}
                <Modal
                    open={successModalOpen}
                    closable={false} footer={null} centered width={440}
                    styles={{
                        content: {
                            background: '#161C1C',
                            border: '1px solid rgba(121,167,165,0.18)',
                            borderRadius: '16px',
                            padding: '48px 40px 40px'
                        },
                        mask: { backdropFilter: 'blur(4px)', background: 'rgba(13,15,20,0.75)' }
                    }}
                >
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            position: 'relative',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 28
                        }}>
                            <div style={{
                                position: 'absolute',
                                width: 88, height: 88,
                                borderRadius: '50%',
                                border: '1px solid rgba(121,167,165,0.2)',
                                animation: 'modalRing 2.4s ease-in-out infinite'
                            }} />
                            <div style={{
                                width: 68, height: 68,
                                borderRadius: '50%',
                                background: 'rgba(121,167,165,0.08)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <CheckCircleOutlined style={{ fontSize: 32, color: '#79A7A5' }} />
                            </div>
                        </div>
                        <Title level={3} style={{ color: '#F0F8F7', margin: '0 0 8px', fontFamily: 'Space Grotesk', fontSize: '22px' }}>
                            Claim Submitted Successfully
                        </Title>
                        <Text style={{ color: '#6B8585', fontSize: '13px', display: 'block', marginBottom: 16 }}>
                            Your documents have been queued for adjudication.
                        </Text>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: 8,
                            background: 'rgba(121,167,165,0.06)',
                            border: '1px solid rgba(121,167,165,0.18)',
                            borderRadius: '8px', padding: '7px 16px', marginBottom: 32
                        }}>
                            <Text style={{ color: '#6B8585', fontSize: '11px', textTransform: 'uppercase' }}>IP NO</Text>
                            <Text style={{ color: '#79A7A5', fontSize: '14px', fontFamily: 'monospace', fontWeight: 600 }}>
                                {submittedIpNo}
                            </Text>
                        </div>
                        <Divider style={{ borderColor: 'rgba(121,167,165,0.08)', margin: '0 0 24px' }} />
                        <Button type="primary" block size="large" icon={<PlusOutlined />} onClick={resetForm} style={{
                            height: 48, borderRadius: 8,
                            background: '#79A7A5', borderColor: '#79A7A5', color: '#0D0F14', fontWeight: 700
                        }}>
                            Upload New Documents
                        </Button>
                    </div>
                </Modal>

                {/* Rules Not Configured Modal */}
                <Modal
                    open={rulesModalVisible}
                    onCancel={() => setRulesModalVisible(false)}
                    footer={null} width={450} centered
                    closeIcon={<CloseOutlined style={{ color: '#6B8585' }} />}
                    styles={{
                        content: {
                            background: '#0D1515',
                            border: '1px solid rgba(121,167,165,0.15)',
                            borderRadius: 12, padding: 0, overflow: 'hidden'
                        },
                        mask: { backdropFilter: 'blur(6px)', background: 'rgba(8,10,12,0.85)' }
                    }}
                >
                    <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(121,167,165,0.1)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <WarningOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
                            <div>
                                <Title level={5} style={{ color: '#F0F8F7', margin: 0, fontFamily: 'Space Grotesk' }}>
                                    Configuration Required
                                </Title>
                                <Text style={{ color: '#A0B5B4', fontSize: 11, fontFamily: 'monospace' }}>
                                    SYSTEM ALERT · {targetPolicy}
                                </Text>
                            </div>
                        </div>
                    </div>
                    <div style={{ padding: '24px' }}>
                        <Text style={{ color: '#A0B5B4', fontSize: 13, lineHeight: 1.6, display: 'block' }}>
                            This policy does not have any adjudication rules configured.
                            Kindly inform the concerned insurer to configure rules for your selected policy.
                        </Text>
                        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                onClick={() => setRulesModalVisible(false)}
                                style={{
                                    background: 'rgba(121,167,165,0.06)',
                                    border: '1px solid rgba(121,167,165,0.2)',
                                    color: '#79A7A5', borderRadius: 7, fontSize: 12,
                                    height: 36, fontFamily: 'monospace', fontWeight: 600, textTransform: 'uppercase'
                                }}
                            >
                                Understood
                            </Button>
                        </div>
                    </div>
                </Modal>

                <style>{`@keyframes modalRing { 0%, 100% { transform: scale(1); opacity: 0.5; } 50% { transform: scale(1.4); opacity: 0; } }`}</style>
            </div>

            {/* Right pane: Member level exclusion, width 800 */}
            <div style={{
                width: 800,
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-strong)',
                borderRadius: '12px',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <Title level={4} style={{ color: '#F0F8F7', marginBottom: 12, fontFamily: 'Space Grotesk' }}>
                    Member level exclusion
                </Title>
                <Input.TextArea
                    rows={10}
                    value={memberLevelTerm}
                    onChange={(e) => setMemberLevelTerm(e.target.value)}
                    disabled={isDisabled}
                    placeholder="Enter member level term (if existing)"
                    style={{
                        background: '#161C1C',
                        borderColor: 'var(--border-strong)',
                        color: '#F0F8F7',
                        borderRadius: '6px',
                        resize: 'none'
                    }}
                />
            </div>
        </div>
    );
};

export default UploadPage;